import {
  ftthCaixaService,
  FtthCaixa,
  CtoPortWithClient,
  CtoPortClientInfo,
  GetClientsByCtoData
} from '../services/ftthCaixaService';
import ConnectionService from '../services/connectionService';
import { CaixaPort } from '../types/caixaPort';

export interface FetchCaixaPortsOptions {
  /** Porta ocupada por este equipamento é tratada como disponível (ex.: troca de CTO) */
  excludeEquipmentId?: number;
}

function mapPortInfo(info: CtoPortClientInfo | null, status: CaixaPort['status']): CaixaPort['info'] {
  if (!info) return undefined;

  if (status === 'disabled') {
    return {
      reason: info.reason || 'Desabilitada',
      date: info.date ?? undefined
    };
  }

  if (status === 'occupied') {
    const clientName = info.client_name || undefined;
    return {
      client_name: clientName,
      client_full_name: clientName,
      client_id: info.client_id ?? undefined,
      contract_id: info.contract_id ?? undefined,
      contract_title: info.contract_title ?? undefined,
      circuito_identificacao: info.circuito_identificacao?.toString(),
      circuito_id: info.circuito_id ?? undefined,
      rxPower: info.rx_power ?? undefined,
      modelo_tipo: info.modelo_tipo ?? undefined,
      service_id: info.contrato_servico_id ?? undefined,
      service_status: info.service_status ?? undefined,
      service_type: info.service_type ?? undefined
    };
  }

  return undefined;
}

export function mapCtoPortsToCaixaPorts(
  ports: CtoPortWithClient[],
  options?: FetchCaixaPortsOptions
): CaixaPort[] {
  return ports.map((port) => {
    const isExcludedOccupant =
      options?.excludeEquipmentId &&
      port.status === 'occupied' &&
      port.info?.equipment_id === options.excludeEquipmentId;

    if (isExcludedOccupant) {
      return {
        port_number: port.port_number,
        status: 'available'
      };
    }

    return {
      port_number: port.port_number,
      status: port.status,
      info: mapPortInfo(port.info, port.status)
    };
  });
}

export async function fetchCaixaPortsData(
  caixaId: number,
  options?: FetchCaixaPortsOptions
): Promise<GetClientsByCtoData> {
  const response = await ftthCaixaService.getClientsByCto(caixaId);

  if (!response.success || !response.data) {
    throw new Error(response.message || 'Erro ao carregar portas da caixa');
  }

  return response.data;
}

export async function fetchCaixaPorts(
  caixaId: number,
  options?: FetchCaixaPortsOptions
): Promise<CaixaPort[]> {
  const data = await fetchCaixaPortsData(caixaId, options);
  return mapCtoPortsToCaixaPorts(data.ports, options);
}

export async function enrichCaixaPortsConnection(ports: CaixaPort[]): Promise<CaixaPort[]> {
  const occupiedEntries = ports
    .filter((port) => port.status === 'occupied' && port.info?.contract_id)
    .map((port) => ({
      portNumber: port.port_number,
      contractId: port.info!.contract_id!,
      serviceId: port.info?.service_id,
    }));

  await Promise.all(
    occupiedEntries.map(async (entry) => {
      const port = ports.find((p) => p.port_number === entry.portNumber);
      if (!port?.info) return;

      try {
        const response = await ConnectionService.getByContract(entry.contractId, {
          onlyActive: true,
          limit: 1,
          ...(entry.serviceId ? { serviceId: entry.serviceId } : {}),
        });
        port.info.connection_status = response.data?.length ? 'Online' : 'Offline';
      } catch (error) {
        console.warn(`Erro ao buscar conexao do contrato ${entry.contractId}`, error);
        port.info.connection_status = 'N/A';
      }
    }),
  );

  return ports;
}

export async function loadCaixaPortsData(
  caixaId: number,
  options?: FetchCaixaPortsOptions
): Promise<{ caixa: FtthCaixa; ports: CaixaPort[]; stats: Pick<GetClientsByCtoData, 'occupied_ports_count' | 'available_ports_count' | 'disabled_ports_count'> }> {
  const [caixaResponse, portsData] = await Promise.all([
    ftthCaixaService.getById(caixaId),
    fetchCaixaPortsData(caixaId, options)
  ]);

  if (!caixaResponse.success || !caixaResponse.data) {
    throw new Error('Caixa não encontrada');
  }

  return {
    caixa: caixaResponse.data,
    ports: mapCtoPortsToCaixaPorts(portsData.ports, options),
    stats: {
      occupied_ports_count: portsData.occupied_ports_count,
      available_ports_count: portsData.available_ports_count,
      disabled_ports_count: portsData.disabled_ports_count
    }
  };
}
